// AdminClubEventEditorPage — create / edit / archive / restore a club event.
// Feb 2026. Reuses the same visual language as other event editors.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CaretLeft, Trash, Archive, ArrowUUpLeft, UploadSimple, ImageSquare } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { clubEventsAPI } from '../lib/clubEventsApi';
import EventFlyerUpload from '../components/events/EventFlyerUpload';
import EventPhotoManager from '../components/events/EventPhotoManager';
import EventManagerAssignment from '../components/events/EventManagerAssignment';
import CommunicateCard from '../components/events/CommunicateCard';
import ManagerSelectField from '../components/events/ManagerSelectField';
import { adminAPI } from '../lib/api';

// Client-side flyer guards — must mirror EventFlyerUpload + backend so the
// user gets an immediate rejection when staging a flyer during Create.
const FLYER_ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const FLYER_MAX_BYTES = 5 * 1024 * 1024;

// A local ISO<->parts helper so we don't need moment/dayjs. Times are
// entered in the admin's local timezone and serialized to ISO on save.
function isoToParts(iso) {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const p = (n) => String(n).padStart(2, '0');
  return { date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
           time: `${p(d.getHours())}:${p(d.getMinutes())}` };
}
function partsToIso(date, time) {
  if (!date) return null;
  const t = time || '00:00';
  const local = new Date(`${date}T${t}`);
  return Number.isNaN(local.getTime()) ? null : local.toISOString();
}

const empty = {
  name: '', description: '', location_text: '',
  starts_at: '', ends_at: '',
  cta_label: '', cta_url: '',
  featured_on_home: false, featured_order: '',
  rsvp_enabled: false, category: '', highlight_text: '',
  payment_enabled: false, entry_fee_dollars: '', payment_type: '', payment_link: '',
};

const CATEGORY_OPTS = [
  { value: '',        label: 'None' },
  { value: 'social',  label: 'Social' },
  { value: 'tennis',  label: 'Tennis' },
  { value: 'charity', label: 'Charity' },
  { value: 'club',    label: 'Club' },
];

const PAYMENT_METHODS = [
  { value: '',       label: 'Choose method…' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'venmo',  label: 'Venmo' },
  { value: 'zelle',  label: 'Zelle' },
  { value: 'cash',   label: 'Cash' },
];

export default function AdminClubEventEditorPage({ embedded = false }) {
  const { id: routeId } = useParams();
  const isNew = routeId === 'new' || !routeId;
  const navigate = useNavigate();
  const { user, managedEvents } = useAuth();
  const [ev, setEv] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  // Staged flyer used only during Create — the file is held client-side
  // until we have an event id, then uploaded via the same
  // POST /api/club-events/{id}/flyer endpoint the editor uses.
  const [pendingFlyer, setPendingFlyer] = useState(null); // File | null
  const [pendingFlyerPreview, setPendingFlyerPreview] = useState(null); // object URL
  // Universal Event Manager pre-selection for CREATE mode. Applied via the
  // universal assignment API right after clubEventsAPI.create() resolves.
  const [managerProfileId, setManagerProfileId] = useState(null);

  const load = useCallback(() => {
    if (isNew) return;
    clubEventsAPI.get(routeId).then((r) => {
      const d = r.data;
      setEv(d);
      const s = isoToParts(d.starts_at); const e = isoToParts(d.ends_at);
      setForm({
        name: d.name || '',
        description: d.description || '',
        location_text: d.location_text || '',
        starts_at_date: s.date, starts_at_time: s.time,
        ends_at_date: e.date, ends_at_time: e.time,
        cta_label: d.cta_label || '',
        cta_url: d.cta_url || '',
        featured_on_home: !!d.featured_on_home,
        featured_order: d.featured_order == null ? '' : String(d.featured_order),
        rsvp_enabled: !!d.rsvp_enabled,
        category: d.category || '',
        highlight_text: d.highlight_text || '',
        payment_enabled: !!d.payment_enabled,
        entry_fee_dollars: d.entry_fee_cents == null ? '' : (d.entry_fee_cents / 100).toString(),
        payment_type: d.payment_type || '',
        payment_link: d.payment_link || '',
      });
    }).catch(() => toast.error('Failed to load club event.'));
  }, [routeId, isNew]);
  useEffect(() => { load(); }, [load]);

  if (!user) return <div className="text-sm text-[#6a766e]">Loading…</div>;
  const isAdmin = user.role === 'admin';
  const isEventManager = !isNew && Array.isArray(managedEvents) && managedEvents.some(
    (m) => m.event_kind === 'club_event' && m.event_id === routeId,
  );
  const canManage = isAdmin || isEventManager;
  if (!canManage) return <Navigate to="/" replace />;
  const isArchived = ev?.status === 'archived';

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const save = async () => {
    if (!form.name.trim()) return toast.error('Title is required.');
    const startISO = partsToIso(form.starts_at_date, form.starts_at_time);
    if (!startISO) return toast.error('Start date is required.');
    // Payment: only send fields when the toggle is on. Off → send
    // payment_enabled=false and let the server null out the three columns.
    let paymentPayload;
    if (form.payment_enabled) {
      // Validate: method required.
      if (!form.payment_type) return toast.error('Choose a payment method.');
      // Amount: blank/empty → null (open-ended). Otherwise convert to cents.
      let cents = null;
      const raw = (form.entry_fee_dollars || '').trim();
      if (raw !== '') {
        const num = Number(raw);
        if (!Number.isFinite(num) || num < 0) return toast.error('Amount must be a non-negative number.');
        cents = Math.round(num * 100);
      }
      paymentPayload = {
        payment_enabled: true,
        entry_fee_cents: cents,
        payment_type: form.payment_type,
        // Stripe uses Embedded Checkout on the event page — no external
        // link is needed or accepted.
        payment_link: form.payment_type === 'stripe'
          ? null
          : ((form.payment_link || '').trim() || null),
      };
    } else {
      paymentPayload = { payment_enabled: false };
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      location_text: form.location_text.trim() || null,
      starts_at: startISO,
      ends_at: partsToIso(form.ends_at_date, form.ends_at_time),
      cta_label: (form.cta_label || '').trim() || null,
      cta_url:   (form.cta_url   || '').trim() || null,
      featured_on_home: !!form.featured_on_home,
      featured_order: form.featured_on_home && form.featured_order !== '' ? Number(form.featured_order) : null,
      rsvp_enabled: !!form.rsvp_enabled,
      category: form.category || null,
      highlight_text: (form.highlight_text || '').trim() || null,
      ...paymentPayload,
    };
    setBusy(true);
    try {
      const r = isNew ? await clubEventsAPI.create(payload) : await clubEventsAPI.update(routeId, payload);
      const created = r.data;
      // Create-mode flyer upload: chain the staged file to the newly created
      // club event. If the upload fails we DO NOT delete the event — we
      // navigate to the editor and surface a warning so Admin can retry.
      let flyerWarning = null;
      let managerWarning = null;
      if (isNew && pendingFlyer) {
        try {
          await clubEventsAPI.uploadFlyer(created.id, pendingFlyer);
        } catch (fe) {
          const d = fe.response?.data?.detail;
          flyerWarning = typeof d === 'string' ? d : (d?.message || 'Flyer upload failed.');
        }
      }
      if (isNew && managerProfileId) {
        try {
          await adminAPI.assignEventManager('club_event', created.id, managerProfileId);
        } catch (me) {
          const d = me.response?.data?.detail;
          managerWarning = typeof d === 'string' ? d : (d?.message || 'Assignment failed.');
        }
      }
      if (isNew) {
        // Free the object URL before navigation.
        if (pendingFlyerPreview) URL.revokeObjectURL(pendingFlyerPreview);
        setPendingFlyer(null); setPendingFlyerPreview(null);
        if (flyerWarning) {
          toast.success('Club event created.');
          toast.error(`Flyer upload failed: ${flyerWarning}. Upload the flyer again from the editor.`,
            { duration: 8000, id: `ce-flyer-fail-${created.id}` });
        } else {
          toast.success(pendingFlyer ? 'Club event created with flyer.' : 'Club event created.');
        }
        if (managerWarning) {
          toast.error(`Event created, but Event Manager could not be assigned: ${managerWarning}. You can retry from Manage.`,
            { duration: 8000, id: `ce-mgr-fail-${created.id}` });
        }
        navigate(`/admin/club-events/${created.id}`, { replace: true });
      } else {
        toast.success('Club event saved.');
        load();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
                : Array.isArray(detail) ? (detail[0]?.msg || 'Save failed')
                : (detail?.message || 'Save failed');
      toast.error(msg);
    } finally { setBusy(false); }
  };

  // Stage a flyer during Create. Validates on the client so Admin gets an
  // immediate response before the network round trip.
  const stageFlyer = (file) => {
    if (!file) return;
    if (!FLYER_ALLOWED_TYPES.includes(file.type)) {
      toast.error('Please choose a JPG, PNG, or WEBP image.');
      return;
    }
    if (file.size > FLYER_MAX_BYTES) {
      toast.error('That image is over 5 MB. Please choose a smaller file.');
      return;
    }
    if (pendingFlyerPreview) URL.revokeObjectURL(pendingFlyerPreview);
    setPendingFlyer(file);
    setPendingFlyerPreview(URL.createObjectURL(file));
  };
  const clearStagedFlyer = () => {
    if (pendingFlyerPreview) URL.revokeObjectURL(pendingFlyerPreview);
    setPendingFlyer(null);
    setPendingFlyerPreview(null);
  };

  const uploadFlyer = async (file) => {
    if (!file || isNew || !ev) return;
    // Shared EventFlyerUpload component handles toasts on success/failure.
    await clubEventsAPI.uploadFlyer(routeId, file);
    load();
  };
  const removeFlyer = async () => {
    if (!ev) return;
    await clubEventsAPI.deleteFlyer(routeId);
    load();
  };
  const doArchive = async () => {
    if (!window.confirm('Archive this club event? It will be hidden from Events discovery and the Home Featured hero.')) return;
    await clubEventsAPI.archive(routeId); toast.success('Archived.'); load();
  };
  const doRestore = async () => {
    await clubEventsAPI.restore(routeId); toast.success('Restored.'); load();
  };
  const doDelete = async () => {
    if (!window.confirm('Permanently delete this club event? This cannot be undone.')) return;
    await clubEventsAPI.remove(routeId); toast.success('Deleted.'); navigate('/admin/events', { replace: true });
  };

  return (
    <div className="space-y-4" data-testid="admin-club-event-editor">
      {!embedded && (
      <div className="flex items-center justify-between">
        <Link to="/admin/events" className="inline-flex items-center gap-1 text-[12px] text-[#013523]"><CaretLeft size={12} /> Events Hub</Link>
        <div className="flex gap-2">
          {!isNew && isAdmin && (isArchived
            ? <button onClick={doRestore} data-testid="cee-restore" className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs flex items-center gap-1"><ArrowUUpLeft size={12} /> Restore</button>
            : <button onClick={doArchive} data-testid="cee-archive" className="px-3 py-1.5 rounded-full border border-[#e3dcd1] text-[#013523] text-xs flex items-center gap-1"><Archive size={12} /> Archive</button>
          )}
          {!isNew && isAdmin && (
            <button onClick={doDelete} data-testid="cee-delete" className="px-3 py-1.5 rounded-full border border-red-300 text-red-700 text-xs flex items-center gap-1"><Trash size={12} /> Delete</button>
          )}
        </div>
      </div>
      )}

      {!embedded && (
        <h1 className="text-xl font-bold text-[#013523]">{isNew ? 'New Club Event' : (ev?.name || 'Club Event')}</h1>
      )}
      {isArchived && (
        <div className="rounded-xl bg-[#f3f1ec] border border-[#e3dcd1] px-3 py-2 text-[12px] text-[#6a766e]">
          Archived — read-only. Restore to edit.
        </div>
      )}

      {/* Event Manager — Admin-only. Non-admin managers reach this page
          for their own event but can't reassign the manager. In CREATE
          mode we render an inline picker; the selection is applied via
          the universal assignment API immediately after clubEventsAPI
          .create() resolves. */}
      {!isNew && (
        <EventManagerAssignment eventKind="club_event" eventId={routeId} isAdmin={isAdmin} />
      )}
      {!isNew && isAdmin && (
        <CommunicateCard eventKind="club_event" eventId={routeId} eventName={ev?.name} isAdmin={isAdmin} />
      )}
      {isNew && isAdmin && (
        <section className="bg-[#fbfaf8] border border-[#e3dcd1] rounded-2xl p-5">
          <ManagerSelectField
            value={managerProfileId}
            onChange={(pid) => setManagerProfileId(pid)}
            testIdPrefix="cee-new-mgr"
          />
        </section>
      )}

      <fieldset disabled={isArchived} className="space-y-4">
        {/* Flyer manager.
            After create: shared EventFlyerUpload (event-flyers bucket,
              JPG/PNG/WEBP, up to 5 MB, replace/remove).
            During create: staged picker — the File is held client-side and
              uploaded once the event id exists (see save()). */}
        {isNew ? (
          <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-2" data-testid="cee-flyer-section-new">
            <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Flyer <span className="text-[#9aa39d] normal-case font-normal">(optional — uploaded when you create the event)</span></div>
            <StagedFlyerPicker
              file={pendingFlyer}
              previewUrl={pendingFlyerPreview}
              onPick={stageFlyer}
              onClear={clearStagedFlyer}
            />
          </div>
        ) : (
          <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-2" data-testid="cee-flyer-section">
            <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Flyer</div>
            <EventFlyerUpload
              flyerUrl={ev?.flyer_url || null}
              onUpload={uploadFlyer}
              onRemove={removeFlyer}
              eventName={ev?.name}
              disabled={isArchived}
            />
            {ev?.id && (
              <div className="pt-2 border-t border-[#efe9dd]">
                <EventPhotoManager kind="club_event" eventId={ev.id} disabled={isArchived} />
              </div>
            )}
          </div>
        )}

        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Title</span>
            <input type="text" value={form.name} onChange={setField('name')} maxLength={200} data-testid="cee-name" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Description</span>
            <textarea value={form.description} onChange={setField('description')} rows={4} maxLength={4000} data-testid="cee-description" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" placeholder="What is happening? Who is it for?" />
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Location</span>
            <input type="text" value={form.location_text} onChange={setField('location_text')} maxLength={200} data-testid="cee-location" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" placeholder="Clubhouse Bar" />
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Start date</span>
              <input type="date" value={form.starts_at_date || ''} onChange={setField('starts_at_date')} data-testid="cee-start-date" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Start time</span>
              <input type="time" value={form.starts_at_time || ''} onChange={setField('starts_at_time')} data-testid="cee-start-time" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">End date <span className="text-[#9aa39d]">(opt)</span></span>
              <input type="date" value={form.ends_at_date || ''} onChange={setField('ends_at_date')} data-testid="cee-end-date" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">End time <span className="text-[#9aa39d]">(opt)</span></span>
              <input type="time" value={form.ends_at_time || ''} onChange={setField('ends_at_time')} data-testid="cee-end-time" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" />
            </label>
          </div>
        </div>

        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Call-to-action <span className="text-[#9aa39d] normal-case font-normal">(optional — both fields required to show the button)</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Button label</span>
              <input type="text" value={form.cta_label} onChange={setField('cta_label')} maxLength={60} data-testid="cee-cta-label" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" placeholder="RSVP" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Button URL</span>
              <input type="url" value={form.cta_url} onChange={setField('cta_url')} maxLength={500} data-testid="cee-cta-url" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" placeholder="https://example.com/rsvp" />
            </label>
          </div>
        </div>

        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Category &amp; highlight</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Category <span className="text-[#9aa39d]">(optional)</span></span>
              <select value={form.category || ''} onChange={setField('category')} data-testid="cee-category"
                className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm bg-white">
                {CATEGORY_OPTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Highlight text <span className="text-[#9aa39d]">(optional, shown as a callout — 400 char max)</span></span>
            <textarea value={form.highlight_text} onChange={setField('highlight_text')} rows={2} maxLength={400}
              data-testid="cee-highlight"
              className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
              placeholder="Grab a drink, meet some Rippers, and hang out." />
          </label>
        </div>

        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">RSVP</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.rsvp_enabled} onChange={setBool('rsvp_enabled')} data-testid="cee-rsvp-toggle" />
            <span>Enable member RSVP (I&rsquo;m in / can&rsquo;t make it)</span>
          </label>
          <div className="text-[11.5px] text-[#6a766e]">
            Turning this off later hides the RSVP button, count, and attendee list — existing RSVPs are preserved and will reappear if you re-enable it.
          </div>
          {!isNew && ev?.rsvp_enabled && (
            <AttendeeAdminList
              clubEventId={routeId}
              paymentEnabled={!!ev?.payment_enabled}
              refreshKey={ev?.updated_at}
            />
          )}
        </div>

        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-3">
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Payment</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.payment_enabled} onChange={setBool('payment_enabled')} data-testid="cee-payment-toggle" />
            <span>Enable payment display</span>
          </label>
          <div className="text-[11.5px] text-[#6a766e]">
            Independent from RSVP. Turning off wipes fee, method, and link on save — nothing stale can come back.
          </div>
          {form.payment_enabled && (
            <div className="pt-1 space-y-2" data-testid="cee-payment-fields">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">
                    Amount <span className="text-[#9aa39d]">(USD, optional — leave blank for open donations)</span>
                  </span>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.entry_fee_dollars}
                    onChange={setField('entry_fee_dollars')}
                    data-testid="cee-payment-amount"
                    className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
                    placeholder="e.g. 25.00"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Payment method</span>
                  <select
                    value={form.payment_type}
                    onChange={setField('payment_type')}
                    data-testid="cee-payment-method"
                    className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm bg-white"
                  >
                    {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </label>
              </div>
              {form.payment_type && form.payment_type !== 'cash' && form.payment_type !== 'stripe' && (
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">
                    Payment link <span className="text-[#9aa39d]">(https://…) — shows a Pay/Donate button</span>
                  </span>
                  <input
                    type="url"
                    value={form.payment_link}
                    onChange={setField('payment_link')}
                    maxLength={500}
                    data-testid="cee-payment-link"
                    className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm"
                    placeholder="https://venmo.com/rippers"
                  />
                </label>
              )}
              {form.payment_type === 'stripe' && (
                <div
                  data-testid="cee-payment-stripe-hint"
                  className="rounded-lg bg-[#f0e6cf]/40 border border-[#c9a93a]/40 px-3 py-2 text-[12.5px] text-[#3a2c05]"
                >
                  Members will pay securely on this event page via Stripe Embedded Checkout. No payment link needed.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Home Featured</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.featured_on_home} onChange={setBool('featured_on_home')} data-testid="cee-featured-toggle" />
            <span>Show on the Home Featured hero</span>
          </label>
          {form.featured_on_home && (
            <label className="block max-w-xs">
              <span className="text-[11px] uppercase tracking-wider text-[#6a766e]">Featured order <span className="text-[#9aa39d]">(lower = earlier)</span></span>
              <input type="number" min={1} max={999} value={form.featured_order} onChange={setField('featured_order')} data-testid="cee-featured-order" className="mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm" placeholder="e.g. 2" />
            </label>
          )}
        </div>
      </fieldset>

      {!isArchived && (
        <div className="flex justify-end">
          <button onClick={save} disabled={busy} data-testid="cee-save" className="px-4 py-2 rounded-full bg-[#013523] text-white text-sm disabled:opacity-60">
            {isNew ? 'Create club event' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  );
}

// Create-mode flyer picker. Mirrors EventFlyerUpload's visual language,
// but the File stays client-side until the event id exists — we then
// chain the upload through the same /api/club-events/{id}/flyer endpoint.
function StagedFlyerPicker({ file, previewUrl, onPick, onClear }) {
  const inputRef = useRef(null);
  return (
    <div data-testid="cee-flyer-staged" className="space-y-3">
      {previewUrl ? (
        <div className="relative border border-[#e3dcd1] rounded-xl overflow-hidden bg-[#f7f4ed]" data-testid="cee-flyer-staged-preview">
          <img
            src={previewUrl}
            alt={file?.name || 'Selected flyer'}
            className="block w-full h-auto max-h-[320px] object-contain mx-auto"
          />
          <div className="absolute top-2 right-2 bg-white/90 text-[10px] text-[#013523] px-2 py-0.5 rounded-full font-medium">
            Uploads on create
          </div>
        </div>
      ) : (
        <div
          data-testid="cee-flyer-staged-empty"
          className="border border-dashed border-[#e3dcd1] rounded-xl bg-[#faf7f0] px-4 py-6 flex flex-col items-center justify-center text-center"
        >
          <ImageSquare size={28} weight="duotone" className="text-[#9aa39d] mb-1" />
          <div className="text-[13px] text-[#013523] font-medium">No flyer yet</div>
          <div className="text-[11.5px] text-[#6a766e] mt-0.5">JPG, PNG or WEBP · up to 5 MB</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="sr-only"
          data-testid="cee-flyer-staged-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPick(f);
            // Reset so re-picking the same file still fires onChange.
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={file ? 'cee-flyer-staged-replace' : 'cee-flyer-staged-pick'}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#013523] text-white text-xs min-h-[44px]"
        >
          <UploadSimple size={14} />
          {file ? 'Replace flyer' : 'Choose flyer'}
        </button>
        {file && (
          <button
            type="button"
            onClick={onClear}
            data-testid="cee-flyer-staged-remove"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#e3dcd1] text-[#8a1c1c] text-xs min-h-[44px]"
          >
            <Trash size={14} /> Remove
          </button>
        )}
      </div>
      {file && (
        <div className="text-[11.5px] text-[#6a766e]" data-testid="cee-flyer-staged-name">
          {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
        </div>
      )}
    </div>
  );
}



// Admin-facing attendee list. Read-only in v1 — admins can see who's in
// but do not RSVP on behalf of members. When payment is enabled, admins
// can also toggle each attendee's Paid/Unpaid state (backend authoritative).
function AttendeeAdminList({ clubEventId, paymentEnabled, refreshKey }) {
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(null); // profile_id currently toggling

  useEffect(() => {
    let alive = true;
    clubEventsAPI.listRsvps(clubEventId)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ attendees: [], attendee_count: 0, payment_enabled: false }); });
    return () => { alive = false; };
  }, [clubEventId, refreshKey, tick]);

  const togglePaid = async (profileId, currentlyPaid) => {
    setBusy(profileId);
    try {
      const call = currentlyPaid
        ? clubEventsAPI.markUnpaid(clubEventId, profileId)
        : clubEventsAPI.markPaid(clubEventId, profileId);
      await call;
      toast.success(currentlyPaid ? 'Marked unpaid.' : 'Marked paid.');
      setTick((n) => n + 1);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Update failed.');
    } finally { setBusy(null); }
  };

  if (!data) return <div className="text-[11.5px] text-[#6a766e]" data-testid="cee-attendees-loading">Loading attendees…</div>;
  return (
    <div data-testid="cee-attendees" className="mt-1 pt-2 border-t border-[#f0ece3]">
      <div className="text-[12px] text-[#013523] font-semibold">
        {data.attendee_count} going
      </div>
      {data.attendees.length === 0 ? (
        <div className="text-[11.5px] text-[#6a766e] mt-1">No RSVPs yet.</div>
      ) : paymentEnabled ? (
        // Payment-on: management table with Mark Paid / Mark Unpaid buttons.
        <ol className="mt-1.5 divide-y divide-[#f0ece3] rounded-lg border border-[#e3dcd1] bg-white" data-testid="cee-attendees-list">
          {data.attendees.map((a, i) => (
            <li key={a.profile_id}
                className="flex items-center gap-2 px-3 py-2"
                data-testid={`cee-attendee-row-${a.profile_id}`}>
              <span className="w-5 text-right text-[11.5px] tabular-nums text-[#6a766e]">{i + 1}.</span>
              <span className="flex-1 min-w-0 text-[12.5px] text-[#013523] truncate">
                {a.first_name}{a.last_initial ? ` ${a.last_initial}` : ''}
              </span>
              {a.paid ? (
                <span data-testid={`cee-paid-chip-${a.profile_id}`} className="text-[10.5px] font-medium text-[#015e3f] bg-[#e6f2ec] border border-[#015e3f]/25 rounded-full px-1.5 py-0.5">Paid</span>
              ) : (
                <span data-testid={`cee-unpaid-chip-${a.profile_id}`} className="text-[10.5px] font-medium text-[#7c5b1a] bg-[#f5eed8] border border-[#c9a93a]/40 rounded-full px-1.5 py-0.5">Due</span>
              )}
              <button
                type="button"
                onClick={() => togglePaid(a.profile_id, !!a.paid)}
                disabled={busy === a.profile_id}
                data-testid={a.paid ? `cee-mark-unpaid-${a.profile_id}` : `cee-mark-paid-${a.profile_id}`}
                className="text-[11px] px-2.5 py-1.5 rounded-full border border-[#013523]/60 text-[#013523] hover:bg-[#013523]/5 disabled:opacity-50 min-h-[32px]"
              >
                {a.paid ? 'Mark unpaid' : 'Mark paid'}
              </button>
            </li>
          ))}
        </ol>
      ) : (
        // Payment-off: preserve the compact chip list.
        <ul className="mt-1.5 flex flex-wrap gap-1.5" data-testid="cee-attendees-list">
          {data.attendees.map((a) => (
            <li key={a.profile_id}
                className="text-[11.5px] px-2 py-0.5 rounded-full bg-[#f4efe6] text-[#013523] border border-[#e3dcd1]">
              {a.first_name}{a.last_initial ? ` ${a.last_initial}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
